import * as React from 'react';
import { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Variable } from '@restworld/utility-types';
import { Checkbox, FormLabelWrapper, Heading, Button } from '@restworld/ui-ds-base';
import { TrashIcon } from '@heroicons/react/24/outline';

import { WizardStepProps } from '../../worker-application-wizard.types';
import { WizardDateField, WizardSelectField, WizardTextInput } from '../';
import { useErrorMessage } from '../../wizard-application-wizard.utils';
// import { useSkippedStep } from '../form-inputs.hooks';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';
import { AssociationOptionLabel } from '../../../professional-associations';

const rootKey = 'certificate';
const defaultValue = {};
// certificate_type: '',
// course_name: '',
// corporate_name: '',
// date: '',
export const WizardEighthStep: React.FunctionComponent<WizardStepProps> = () => {
  const { control, trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: rootKey,
  });
  const { t } = useTranslation();
  const certificatesEl = fields.map((field, fieldIndex) => (
    <React.Fragment key={field.id}>
      <div className="flex justify-start gap-4">
        <Button
          disabled={fields.length === 1}
          onClick={() => remove(fieldIndex)}
          variant="secondary-danger"
          StartIcon={TrashIcon}
        />
        <Heading>{`${t('common:certificate')} ${fieldIndex + 1}`}</Heading>
      </div>
      <Fields fieldIndex={fieldIndex} />
      {Boolean(fields.length - 1 === fieldIndex) && (
        <div className="self-center">
          <Button
            onClick={async (e) => {
              e?.stopPropagation();
              await trigger(`${rootKey}.${fieldIndex}`)
                .then((r) => {
                  if (r) append(defaultValue, { focusIndex: fieldIndex + 1 });
                })
                .catch((e) => console.error(e, 'error on form validation'));
            }}
            variant="secondary-restworld"
          >
            + {t('onboarding-page:add_certificate')}
          </Button>
        </div>
      )}
    </React.Fragment>
  ));
  useEffect(() => {
    if (!fields.length) append(defaultValue);
  }, [append, fields.length]);
  return (
    <div className="flex flex-col gap-4 md:h-[26rem] md:overflow-y-auto md:px-1">
      <ProfessionalAssociations />
      {certificatesEl}
    </div>
  );
};
export default WizardEighthStep;

/**
 * Which professional associations the worker belongs to (APCI first).
 *
 * Placed on the certificate step rather than a step of its own: this is the one
 * place in the wizard that already asks about credentials issued by an outside
 * body, down to a "who issued it" field, and the step is `canBeSkipped` so an
 * optional question here costs nobody a completion. Adding a fifteenth step to a
 * fourteen-step onboarding for a question most workers answer "none" to would.
 *
 * Not `WizardMultiSelect`, for the same reason the profile picker is not
 * `CheckboxGroup`: both render only a Variable's label and drop its `icon`, and
 * the logo beside the name is the point of the field.
 *
 * Stored as a flat array of ids; `handleUpdateWorker` maps it to the shape the
 * API expects on the way out.
 */
const ProfessionalAssociations = () => {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const {
    fieldOptions: { professionalAssociations },
  } = useWorkerApplicationCxt();

  if (!professionalAssociations?.length) return null;

  return (
    <FormLabelWrapper id="professional_associations" label={t('common:professional_associations')}>
      <Controller
        name="professional_associations"
        control={control}
        rules={{ required: false }}
        defaultValue={[]}
        render={({ field }) => (
          <ul className="flex flex-col gap-3 w-full m-0 p-0 list-none">
            {professionalAssociations.map((association: Variable) => (
              <li key={association.id}>
                <Checkbox
                  id={association.id}
                  mode="clickable"
                  isChecked={(field.value ?? []).includes(association.id)}
                  title={<AssociationOptionLabel association={association} />}
                  onChange={(event) => {
                    const selected: string[] = field.value ? [...field.value] : [];
                    field.onChange(
                      event.target.checked
                        ? [...selected.filter((id) => id !== association.id), association.id]
                        : selected.filter((id) => id !== association.id)
                    );
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      />
      <p className="m-0 mt-2 text-sm text-gray-600">{t('common:professional_associations_visibility_helper')}</p>
    </FormLabelWrapper>
  );
};

const Fields = ({ fieldIndex }: { fieldIndex: number }) => {
  const {
    fieldOptions: { certificateType },
  } = useWorkerApplicationCxt();
  return (
    <>
      <CertificateType fieldIndex={fieldIndex} options={certificateType} />
      <CourseName fieldIndex={fieldIndex} />
      <CorporateName fieldIndex={fieldIndex} />
      <ReleaseDate fieldIndex={fieldIndex} />
    </>
  );
};
const CertificateType = React.memo(({ options, fieldIndex }: { options: Variable[]; fieldIndex: number }) => {
  // const isSkipped = useSkippedStep();
  const errorMessage = useErrorMessage({
    fieldIndex,
    rootKey,
    id: 'certificate_type',
  });
  const { t } = useTranslation();
  return (
    <WizardSelectField
      options={options}
      placeholder={t('onboarding-page:choose_certificate')}
      id={`certificate.${fieldIndex}.certificate_type` as const}
      formLabel={t('onboarding-page:type_of_certificate')}
      rules={{ required: false }}
      errorMessage={errorMessage}
      mainTitle={t('onboarding-page:your_study_path')}
      subTitle={t('onboarding-page:select_one_or_more_options')}
      testId="certificate-type-options"
      inputTestId="certificate-type-input"
    />
  );
});
const CourseName = ({ fieldIndex }: { fieldIndex: number }) => {
  // const isSkipped = useSkippedStep();
  const errorMessage = useErrorMessage({
    fieldIndex,
    rootKey,
    id: 'course_name',
  });
  const { t } = useTranslation();

  return (
    <WizardTextInput
      id={`${rootKey}.${fieldIndex}.course_name`}
      placeholder={t('onboarding-page:title_of_the_course_taken')}
      type={'text'}
      rules={{ required: false }}
      label={t('onboarding-page:course_name')}
      errorMessage={errorMessage}
      testId="course-name-input"
    />
  );
};
const CorporateName = ({ fieldIndex }: { fieldIndex: number }) => {
  // const isSkipped = useSkippedStep();
  const errorMessage = useErrorMessage({
    fieldIndex,
    rootKey,
    id: 'corporate_name',
  });
  const { t } = useTranslation();
  return (
    <WizardTextInput
      id={`${rootKey}.${fieldIndex}.corporate_name`}
      placeholder={t('onboarding-page:who_issued_the_course')}
      type={'text'}
      rules={{ required: false }}
      label={t('common:body')}
      errorMessage={errorMessage}
      testId="corporate-name-input"
    />
  );
};
const ReleaseDate = ({ fieldIndex }: { fieldIndex: number }) => {
  const errorMessage = useErrorMessage({
    fieldIndex,
    rootKey,
    id: 'date',
  });
  // const isSkipped = useSkippedStep();
  const { t } = useTranslation();

  return (
    <FormLabelWrapper id={'date'} label={t('common:release_date')}>
      <WizardDateField
        id={`${rootKey}.${fieldIndex}.date`}
        rules={{ required: false }}
        errorMessage={errorMessage}
        testId="release-date-picker-box"
      />
    </FormLabelWrapper>
  );
};
